import { AuthenticationContext } from "react-adal";
import jwtDecode from "jwt-decode";
import { externalApiCall } from "./api";
import empty from "is-empty";
import { BASE_API_URL } from "../constants";

const adalConfig = {
  tenant: "84624538-50aa-4736-89a2-5fbe3b9a04bf",
  clientId: "46499ae6-8546-4b05-bb22-38b2e96b2de8",
  endpoints: {
    api: BASE_API_URL,
    "https://graph.microsoft.com": "https://graph.microsoft.com",
  },
  postLogoutRedirectUri: window.location.origin,
  redirectUri: window.location.origin,
  cacheLocation: "localStorage",
};

export const authContext = new AuthenticationContext(adalConfig);

export const getToken = () => {
  return authContext.getCachedToken(authContext.config.clientId);
};

// ADAL's silent renewal: a hidden iframe against the login endpoint. Resolves
// null rather than rejecting, because every caller here treats "no token" as a
// condition to handle, not a crash. The try wraps the call itself since ADAL
// can throw synchronously when the iframe cannot be created at all.
function acquireTokenSilently(resource) {
  return new Promise(function (resolve) {
    try {
      // ADAL refuses to open the renewal iframe while its in-memory user is
      // null, and runWithAdal's cache check is `!token || !getCachedUser()`,
      // so whenever the app token is the thing that expired the `||` short
      // circuits and the user is never hydrated. Hydrate it here. _createUser
      // only decodes the cached id_token and checks its aud, so an expired
      // id_token still supplies the login hint the iframe needs. No cached
      // id_token at all leaves the user null, which is a real sign-in.
      authContext.getCachedUser();
      authContext.acquireToken(resource, function (error, token) {
        if (error || !token) {
          resolve(null);
        } else {
          resolve(token);
        }
      });
    } catch {
      resolve(null);
    }
  });
}

async function getProfileToken() {
  // Avatar is cosmetic; a Graph token failure must not block sign-in.
  const token = await acquireTokenSilently("https://graph.microsoft.com");
  return token ? getProfilePic(token) : null;
}

async function getProfilePic(token) {
  return new Promise((resolve) => {
    externalApiCall("get", "https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: "Bearer " + token,
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers":
          "Origin, X-Requested-With, Content-Type, Accept",
      },
      responseType: "blob",
    })
      .then(function (res) {
        const img = !empty(res) ? window.URL.createObjectURL(res) : "";
        resolve(img);
      })
      .catch(function () {
        resolve(null);
      });
  });
}

// One interactive retry per tab session — a second failure means
// redirecting again would just loop. The sessionStorage GLOBAL is resolved
// inside the try because browser policy can make the property access itself
// throw; storage that is missing or throwing counts as already-attempted,
// so the worst case is the error page, never a redirect loop.
export function shouldAttemptInteractiveLogin(storage) {
  try {
    const s = storage || window.sessionStorage;
    if (s.getItem("interactiveLoginAttempted")) return false;
    s.setItem("interactiveLoginAttempted", "1");
    return true;
  } catch (e) {
    return false;
  }
}

export function clearInteractiveLoginMarker(storage) {
  try {
    (storage || window.sessionStorage).removeItem("interactiveLoginAttempted");
  } catch (e) {
    // best-effort
  }
}

export async function getUser() {
  // A cache miss is the ordinary state here, not a failure. ADAL access tokens
  // last about an hour, so any visit after that has nothing cached. Renew
  // silently first. Only a silent renewal that also fails means the user
  // genuinely has to sign in again. Going straight to an interactive redirect
  // made a routine token lapse look like a broken session.
  const token = getToken() || (await acquireTokenSilently(adalConfig.clientId));
  const decoded = await jwtDecode(token);
  const header = await jwtDecode(token, { header: true });
  // The avatar is decoration — never let a failed Graph token call
  // (Safari blocks the silent auth iframe) reject the whole sign-in.
  const pic = await getProfileToken().catch(() => null);

  return {
    email: decoded.upn,
    name: decoded.name,
    header: header,
    token: token,
    pic: pic,
  };
}
